import { useAPI, useAsyncHandler } from '~/hooks/common';

import { API_ENDPOINT } from '~/constants';

import type {
  TypeGetReturnOrdersReturnReceiptIdParams,
  TypeGetReturnOrdersReturnReceiptIdResponse,
  TypeReturnDetailData,
} from '~/types/claim.types';
import type { TypeErrorResponseData } from '~/types/common.types';

/**
 * [반품 신청/조회] 반품 신청 상세 조회 api 요청 훅
 */
const useGetReturnOrdersReturnReceiptId = () => {
  const { runAsync } = useAsyncHandler();

  const getReturnOrdersReturnReceiptId = useAPI<
    TypeGetReturnOrdersReturnReceiptIdParams,
    TypeGetReturnOrdersReturnReceiptIdResponse
  >({
    endpoint: API_ENDPOINT.CLAIM.GET_RETURN_ORDERS_RETURN_RECEIPT_ID,
    method: 'get',
  });

  /**
   * 상품 코드가 같은 단위끼리 묶어서 포맷팅하는 함수
   * - 상품코드 기준으로 그룹핑
   * - 검수 단위 별 정보는 inspectionDetails 배열에 추가
   */
  const formatReturnDetailData = (data: TypeGetReturnOrdersReturnReceiptIdResponse['data']): TypeReturnDetailData => {
    const { returnGoodsInspections } = data;

    const groupedByGoodsCode: Record<string, TypeReturnDetailData['returnGoodsInspections'][number]> = {};

    returnGoodsInspections.forEach((inspection) => {
      const {
        goodsCode,
        goodsName,
        returnQuantity,
        returnReason,
        inspectionQuantity,
        inboundStatus,
        inspectionStatus,
        inspectionImageUrls,
        inspectionAt,
        unlistedYn,
      } = inspection;

      if (!groupedByGoodsCode[goodsCode]) {
        groupedByGoodsCode[goodsCode] = {
          goodsCode,
          goodsName,
          returnQuantity,
          returnReason,
          inspectionDetails: [],
        };
      }

      // 검수 단위 별 정보 추가
      groupedByGoodsCode[goodsCode].inspectionDetails.push({
        inspectionQuantity,
        inboundStatus,
        inspectionStatus,
        inspectionImageUrls,
        inspectionAt,
        unlistedYn,
      });
    });

    const formattedReturnGoodsInspections = Object.values(groupedByGoodsCode);

    return {
      ...data,
      returnGoodsInspections: formattedReturnGoodsInspections,
    };
  };

  const requestReturnOrdersDetail = async (
    { returnReceiptId }: TypeGetReturnOrdersReturnReceiptIdParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeReturnDetailData) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    }
  ) => {
    runAsync(() => getReturnOrdersReturnReceiptId({ pathParams: `/${returnReceiptId}` }), {
      onSuccess: ({ data }) => {
        const formattedData = formatReturnDetailData(data);
        onSuccess?.(formattedData);
      },
      onError: (error: TypeErrorResponseData['error']) => onError?.(error),
    });
  };

  return { requestReturnOrdersDetail };
};

export default useGetReturnOrdersReturnReceiptId;
